import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { DateTime } from "luxon";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { useRemoteFetchUserReport } from "../../hooks/Remote/User/useRemoteFetchUserReport";
import { useRemoteUserHomeOfficeInfo } from "../../hooks/Remote/User/useRemoteUserHomeOfficeInfo";
import { selectVisitorManagementEnabled } from "../Login/selectors/selectVisitorManagementEnabled";
import { selectConferenceZoneBookingEnabled } from "../Login/selectors/selectConferenceZoneBookingEnabled";
import { selectDashboard2PlaceQuickActions } from "./selectors/selectDashboard2PlaceQuickActions";
import { ScheduleCreateOnCalendarDialog } from "../../components/Schedule/ScheduleCalendar/ScheduleCreateOnCalendarDialog/ScheduleCreateOnCalendarDialog.component";
import { ScheduleSelectionDialog } from "../../components/Schedule/ScheduleCalendar/ScheduleSelectionDialog/ScheduleSelectionDialog.component";
import { ScheduleUnlockDialog } from "../../components/Schedule/ScheduleCalendar/ScheduleUnlockDialog/ScheduleUnlockDialog";
import { ColleagueWorkplaceFloorDialog } from "./ColleagueWorkplaceFloor/colleague-workplace-floor-dialog.component";
import BookingSvgDialog from "../../components/Svg/svg-dialog.component";
import { mergeScheduleFloorPlanOccupancyUsers } from "../../components/Schedule/functions/schedule-floor-plan-occupancy-icon.function";
import type { BookingScheduleInterface } from "../Booking-Form/typings/booking.types";
import Schedule, { type ScheduleEmbedConfig } from "../Schedule/schedule.component";
import { ScheduleViewMode } from "../Schedule/partial/ScheduleViewToggle.partial";
import { ScheduleMode } from "../Schedule/partial/ScheduleModeToggle.partial";
import {
  Dashboard2ScheduleControls,
  Dashboard2ScheduleLayout
} from "./components/Dashboard2ScheduleControls";
import { Dashboard2TopRow } from "./components/Dashboard2TopRow";
import { Dashboard2WeekPlanningGrid } from "./components/Dashboard2WeekPlanningGrid";
import { Dashboard2WelcomeBanner } from "./components/Dashboard2WelcomeBanner";
import { Dashboard2WfhChip } from "./components/Dashboard2WfhChip";
import { Dashboard2ScheduleLoadErrorBanner } from "./components/Dashboard2ScheduleLoadErrorBanner";
import { useDashboard2MobileWorkingEnabled } from "./hooks/useDashboard2MobileWorkingEnabled";
import { useDashboard2MobileWorkingLocation } from "./hooks/useDashboard2MobileWorkingLocation";
import { useDashboard2ScheduleToolbarState } from "./hooks/useDashboard2ScheduleToolbarState";
import { useDashboard2TodayStatus } from "./hooks/useDashboard2TodayStatus";
import { useDashboard2StatusMutations } from "./hooks/useDashboard2StatusMutations";
import { useDashboard2WeekPlanDays } from "./hooks/useDashboard2WeekPlanDays";
import { useDashboard2WeekSchedules } from "./hooks/useDashboard2WeekSchedules";
import type { Dashboard2WeekDay, DayWorkStatusKind } from "./types/dashboard2-work-status.types";
import type { WeekDayCardActionKind } from "./utils/dashboard2-week-day-card.utils";
import {
  getDashboard2WeekRange,
  selectPrimaryTodayBookingForUser,
  storedColleaguesExcludingUser
} from "./utils/dashboard2-schedule.utils";
import { getDashboard2FavoriteLocations } from "./utils/dashboard2-favorite-locations.utils";
import { scrollDashboard2SchedulePanelIntoView } from "./utils/dashboard2-page-scroll.utils";
import { dashboard2ScheduleEmbedSx } from "./dashboard2.styles";
import {
  dashboard2PageCalendarTableSx,
  dashboard2ScheduleSectionNaturalSx,
  dashboard2ScheduleWeekClusterSx,
  dashboard2WeekSectionNaturalSx
} from "./dashboard2-status.styles";

const Dashboard2 = () => {
  const theme = useTheme();
  const {
    sub,
    company: {
      meta: { workplaceBookingEnabled, showBookingStatistics }
    }
  } = useSelector((state: RootState) => state.login.userInformation);

  const visitorManagementEnabled = useSelector(selectVisitorManagementEnabled);
  const conferenceZoneBookingEnabled = useSelector(selectConferenceZoneBookingEnabled);
  const placeBookingTypes = useSelector(selectDashboard2PlaceQuickActions);
  const [weekAnchor, setWeekAnchor] = useState(() => DateTime.now().startOf("week"));
  const [isQuickAutomatedPlaceBooking, setIsQuickAutomatedPlaceBooking] = useState(false);
  const [isUnlockModalOpen, setIsUnlockModalOpen] = useState(false);
  const [unlockSchedule, setUnlockSchedule] = useState<BookingScheduleInterface | null>(null);

  const weekRange = useMemo(() => getDashboard2WeekRange(weekAnchor), [weekAnchor]);

  const scheduleToolbar = useDashboard2ScheduleToolbarState(weekRange.startDate, weekRange.endDate);

  const {
    schedules,
    refetchSchedules,
    isLoading: schedulesLoading,
    scheduleLoadFailed
  } = useDashboard2WeekSchedules(weekAnchor, {
    scheduleMode: scheduleToolbar.scheduleMode,
    selectedColleagues: scheduleToolbar.selectedColleagues,
    selectedResource: scheduleToolbar.selectedResource
  });

  const todayStatus = useDashboard2TodayStatus(schedules, sub);

  const primaryTodaySchedule = useMemo(
    () => selectPrimaryTodayBookingForUser(schedules, sub),
    [schedules, sub]
  );

  const handleClickUnlock = useCallback((schedule: BookingScheduleInterface) => {
    setUnlockSchedule(schedule);
    setIsUnlockModalOpen(true);
  }, []);

  const handleCloseUnlockModal = useCallback(() => {
    setIsUnlockModalOpen(false);
    setUnlockSchedule(null);
  }, []);

  const handleTodayStripUnlock = useCallback(() => {
    if (primaryTodaySchedule) {
      handleClickUnlock(primaryTodaySchedule);
    }
  }, [handleClickUnlock, primaryTodaySchedule]);

  const topRowColleagues = useMemo(
    () => ({
      schedules,
      storedColleagues: storedColleaguesExcludingUser(
        scheduleToolbar.colleagues,
        scheduleToolbar.preStoredColleagueIds,
        sub
      )
    }),
    [schedules, scheduleToolbar.colleagues, scheduleToolbar.preStoredColleagueIds, sub]
  );

  const [reportStartDate, reportEndDate] = useMemo(
    () => [
      DateTime.now().minus({ month: 1 }).toFormat("yyyy-MM-dd"),
      DateTime.now().toFormat("yyyy-MM-dd")
    ],
    []
  );

  const shouldFetchUserReport = workplaceBookingEnabled;
  const { data: userReport } = useRemoteFetchUserReport(
    {
      startDate: reportStartDate,
      endDate: reportEndDate
    },
    shouldFetchUserReport
  );

  const { data: userHomeOfficeInfo, isLoading: userHomeOfficeInfoLoading } =
    useRemoteUserHomeOfficeInfo();

  const favoriteLocations = useMemo(
    () => getDashboard2FavoriteLocations(userReport, showBookingStatistics),
    [showBookingStatistics, userReport]
  );

  const mobileWorkingEnabled = useDashboard2MobileWorkingEnabled();
  const mobileWorkingLocation = useDashboard2MobileWorkingLocation(userReport);

  const weekPlanDays = useDashboard2WeekPlanDays({
    weekAnchor,
    schedules,
    currentUserId: sub,
    storedColleagues: topRowColleagues.storedColleagues
  });

  const handleQuickBookSuccess = useCallback(() => {
    refetchSchedules();
    setIsQuickAutomatedPlaceBooking(true);
  }, [refetchSchedules]);

  const mutations = useDashboard2StatusMutations({
    schedules,
    currentUserId: sub,
    favoriteLocation: favoriteLocations[0],
    mobileWorkingLocation,
    mobileWorkingEnabled,
    refetchSchedules,
    onQuickBookSuccess: handleQuickBookSuccess,
    scheduleMode: scheduleToolbar.scheduleMode
  });

  const handleSelectTodayStatus = useCallback(
    (kind: DayWorkStatusKind) => {
      mutations.handleSelectStatus(kind, DateTime.now());
    },
    [mutations]
  );

  const handleWeekSheetSelectStatus = useCallback(
    (kind: DayWorkStatusKind, day: Dashboard2WeekDay) => {
      mutations.handleSelectStatus(kind, DateTime.fromISO(day.dayKey));
    },
    [mutations]
  );

  const handleWeekDayAction = useCallback(
    (actionKind: WeekDayCardActionKind, day: Dashboard2WeekDay) => {
      mutations.handleWeekDayAction(actionKind, day);
    },
    [mutations]
  );

  const showWfhChip =
    (userHomeOfficeInfo?.[0]?.minMobileWorkingBookingHours ?? 0) > 0 && !userHomeOfficeInfoLoading;

  const handlePreviousWeek = useCallback(() => {
    setWeekAnchor(previous => previous.minus({ weeks: 1 }).startOf("week"));
  }, []);

  const handleNextWeek = useCallback(() => {
    setWeekAnchor(previous => previous.plus({ weeks: 1 }).startOf("week"));
  }, []);

  const handleWeekSelect = useCallback((week: DateTime) => {
    setWeekAnchor(week.startOf("week"));
  }, []);

  const scheduleControlsRef = useRef<HTMLDivElement>(null);
  const [scheduleFilterOpenSignal, setScheduleFilterOpenSignal] = useState(0);

  const handleScheduleLayoutChange = useCallback(
    (layout: Dashboard2ScheduleLayout) => {
      scheduleToolbar.setScheduleLayout(layout);
    },
    [scheduleToolbar.setScheduleLayout]
  );

  const handleShowColleaguesField = useCallback(() => {
    scheduleToolbar.setScheduleMode(ScheduleMode.Colleague);
    scheduleToolbar.setScheduleLayout(Dashboard2ScheduleLayout.Calendar);
    setScheduleFilterOpenSignal(signal => signal + 1);
  }, [scheduleToolbar.setScheduleLayout, scheduleToolbar.setScheduleMode]);

  useEffect(() => {
    const layout = scheduleToolbar.scheduleLayout;
    if (layout !== Dashboard2ScheduleLayout.Calendar && layout !== Dashboard2ScheduleLayout.Table) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      scrollDashboard2SchedulePanelIntoView(scheduleControlsRef.current);
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [scheduleToolbar.scheduleLayout]);

  const handleScheduleViewModeChange = useCallback(
    (mode: ScheduleViewMode) => {
      scheduleToolbar.setScheduleLayout(
        mode === ScheduleViewMode.Table
          ? Dashboard2ScheduleLayout.Table
          : Dashboard2ScheduleLayout.Calendar
      );
    },
    [scheduleToolbar.setScheduleLayout]
  );

  const floorPlanOccupancyUsers = useMemo(
    () =>
      mergeScheduleFloorPlanOccupancyUsers([
        scheduleToolbar.selectedColleagues,
        scheduleToolbar.colleagues
      ]),
    [scheduleToolbar.colleagues, scheduleToolbar.selectedColleagues]
  );

  const sharedToolbarFields = useMemo(
    () => ({
      scheduleMode: scheduleToolbar.scheduleMode,
      onScheduleModeChange: scheduleToolbar.setScheduleMode,
      scheduleViewMode: scheduleToolbar.scheduleViewMode,
      onScheduleViewModeChange: handleScheduleViewModeChange,
      selectedColleagues: scheduleToolbar.selectedColleagues,
      onSelectedColleaguesChange: scheduleToolbar.setSelectedColleagues,
      colleagues: scheduleToolbar.colleagues,
      preStoredColleagues: scheduleToolbar.preStoredColleagueIds,
      scheduleColleaguesIsFetching: scheduleToolbar.scheduleColleaguesIsFetching,
      storedScheduleColleaguesIsFetching: scheduleToolbar.storedScheduleColleaguesIsFetching,
      onStoreSelectedColleagues: scheduleToolbar.handleStoreSelectedColleagues,
      containerRef: scheduleToolbar.containerRef,
      tagLimit: scheduleToolbar.tagLimit,
      selectedResource: scheduleToolbar.selectedResource,
      onSelectedResourceChange: scheduleToolbar.setSelectedResource,
      resourceBookingType: scheduleToolbar.resourceBookingType,
      onResourceBookingTypeChange: scheduleToolbar.setResourceBookingType,
      resourceKind: scheduleToolbar.resourceKind,
      onResourceKindChange: scheduleToolbar.setResourceKind,
      resourceAvailableTypes: scheduleToolbar.resourceAvailableTypes,
      resourceAutocompleteLoading: scheduleToolbar.resourceAutocompleteLoading,
      bookableBookingObjectsError: scheduleToolbar.bookableBookingObjectsError,
      resourceAutocompleteOptions: scheduleToolbar.resourceAutocompleteOptions,
      onResourceFetchEnable: scheduleToolbar.handleResourceFetchEnable,
      resourceLocationCards: scheduleToolbar.resourceLocationCards,
      selectedResourceLocationIds: scheduleToolbar.selectedResourceLocationIds,
      onToggleResourceLocation: scheduleToolbar.toggleResourceLocation,
      onClearResourceLocationFilter: scheduleToolbar.clearResourceLocationFilter
    }),
    [handleScheduleViewModeChange, scheduleToolbar]
  );

  const scheduleEmbed = useMemo(
    (): ScheduleEmbedConfig => ({
      hideToolbar: true,
      ...sharedToolbarFields,
      contentContainerSx: dashboard2ScheduleEmbedSx(theme)
    }),
    [sharedToolbarFields, theme]
  );

  const isWeekLayout = scheduleToolbar.scheduleLayout === Dashboard2ScheduleLayout.Week;
  const pageSx = dashboard2PageCalendarTableSx;
  const weekSectionSx = dashboard2WeekSectionNaturalSx;

  const statusActionsDisabled = schedulesLoading || mutations.isStatusMutationPending;

  const scheduleControls = (
    <Dashboard2ScheduleControls
      layout={scheduleToolbar.scheduleLayout}
      onLayoutChange={handleScheduleLayoutChange}
      filterOpenSignal={scheduleFilterOpenSignal}
      toolbarProps={{
        ...sharedToolbarFields,
        isColleagueMode: scheduleToolbar.isColleagueMode,
        isResourceMode: scheduleToolbar.isResourceMode,
        isFirstAidSelected: false
      }}
    />
  );

  return (
    <Stack
      spacing={2}
      useFlexGap
      data-testid="dashboard2-page"
      aria-busy={schedulesLoading}
      sx={pageSx}
    >
      <Dashboard2WelcomeBanner status={todayStatus} />
      {scheduleLoadFailed ? <Dashboard2ScheduleLoadErrorBanner onRetry={refetchSchedules} /> : null}
      <Box sx={{ flexShrink: 0 }}>
        <Dashboard2TopRow
          todayStatus={todayStatus}
          primaryTodaySchedule={primaryTodaySchedule}
          allSchedules={schedules}
          refetchSchedules={refetchSchedules}
          onClickUnlock={handleTodayStripUnlock}
          colleagueSchedules={topRowColleagues.schedules}
          storedColleagues={topRowColleagues.storedColleagues}
          visitorManagementEnabled={visitorManagementEnabled}
          conferenceZoneBookingEnabled={conferenceZoneBookingEnabled}
          placeBookingTypes={placeBookingTypes}
          onSelectTodayStatus={handleSelectTodayStatus}
          onBookingClick={mutations.handleBookingSummaryClick}
          onPrimaryScheduleClick={mutations.handleScheduleDetailClick}
          onOpenFloorPlan={mutations.openFloorPlanFromSchedule}
          onBookAVisitor={mutations.handleBookAVisitor}
          onBookPlaceType={mutations.handleBookPlaceType}
          mobileWorkingEnabled={mobileWorkingEnabled}
          schedulesLoading={schedulesLoading}
          statusActionsDisabled={statusActionsDisabled}
          onShowColleaguesField={handleShowColleaguesField}
        />
      </Box>

      {isWeekLayout ? (
        <Box sx={dashboard2ScheduleWeekClusterSx} ref={scheduleControlsRef}>
          {scheduleControls}
          <Box sx={weekSectionSx}>
            <Dashboard2WeekPlanningGrid
              weekAnchor={weekAnchor}
              onPreviousWeek={handlePreviousWeek}
              onNextWeek={handleNextWeek}
              onWeekSelect={handleWeekSelect}
              weekDays={weekPlanDays}
              onSelectStatus={handleWeekSheetSelectStatus}
              onPrimaryScheduleClick={mutations.handleScheduleDetailClick}
              onWeekDayAction={handleWeekDayAction}
              onBookingClick={mutations.handleBookingSummaryClick}
              mobileWorkingEnabled={mobileWorkingEnabled}
              isLoading={schedulesLoading}
              statusActionsDisabled={statusActionsDisabled}
            />
          </Box>
        </Box>
      ) : (
        <Box sx={{ flexShrink: 0 }} ref={scheduleControlsRef}>
          {scheduleControls}
        </Box>
      )}

      {!isWeekLayout ? (
        <Box sx={dashboard2ScheduleSectionNaturalSx}>
          <Schedule
            embed={scheduleEmbed}
            isQuickAutomatedPlaceBooking={isQuickAutomatedPlaceBooking}
            setIsQuickAutomatedPlaceBooking={setIsQuickAutomatedPlaceBooking}
          />
        </Box>
      ) : null}

      {isWeekLayout && showWfhChip && userHomeOfficeInfo?.[0] && (
        <Dashboard2WfhChip homeOfficeRatio={userHomeOfficeInfo[0]} />
      )}
      {mutations.selectedBooking.schedule && (
        <ScheduleSelectionDialog
          scheduleMode={mutations.scheduleMode}
          isCalendarView
          isEditable
          isOpen={mutations.selectedBooking.open}
          onClose={mutations.handleCloseBookingDetail}
          schedule={mutations.selectedBooking.schedule}
          schedulePictures={[]}
          pictureIsLoading={false}
          allSchedules={schedules}
          calendarData={[]}
          isFirstAidSelected={false}
          refetchAllSchedule={refetchSchedules}
          onScheduleChange={mutations.openFloorPlanFromSchedule}
          onClickUnlock={() => {
            handleClickUnlock(mutations.selectedBooking.schedule!);
          }}
        />
      )}
      {mutations.isCreateOnCalendar.open ? (
        <ScheduleCreateOnCalendarDialog
          open={mutations.isCreateOnCalendar.open}
          isCreateOnCalendar={mutations.isCreateOnCalendar}
          onClose={mutations.handleCloseCreateDialog}
          hideAssistantTab
        />
      ) : null}
      {mutations.colleagueFloor.open ? (
        <ColleagueWorkplaceFloorDialog
          session={mutations.colleagueFloor}
          onClose={mutations.handleCloseColleagueFloor}
          onBooked={() => {
            mutations.handleCloseColleagueFloor();
            refetchSchedules();
          }}
          onSessionChange={mutations.patchColleagueFloor}
        />
      ) : null}
      {mutations.floorPlan.open ? (
        <BookingSvgDialog
          isShowDialogOpen={mutations.floorPlan.open}
          bookingId={mutations.floorPlan.bookingId}
          bookingType={mutations.floorPlan.bookingType}
          highlight={mutations.floorPlan.highlight}
          occupancySchedule={mutations.floorPlan.occupancySchedule}
          occupancyUsers={floorPlanOccupancyUsers}
          closeFn={mutations.handleCloseFloorPlan}
        />
      ) : null}
      {unlockSchedule?.doorLockData && isUnlockModalOpen ? (
        <ScheduleUnlockDialog
          unlockableData={unlockSchedule.doorLockData}
          open={isUnlockModalOpen}
          onClose={handleCloseUnlockModal}
          title="target"
          bookingId={unlockSchedule.id}
          bookingType={unlockSchedule.bookingType}
          checkedIn={unlockSchedule.checkedIn}
          unCheckedinSchedules={undefined}
          refetchAllSchedule={refetchSchedules}
        />
      ) : null}
    </Stack>
  );
};

export default Dashboard2;
